import { Component, ElementRef, Input, ViewChild, computed, signal } from '@angular/core';
import { Theme, VideoWidget, VideoLayout, WidgetColors } from '../../models/reader-document';
import { DEFAULT_COLORS, DEFAULT_PADDING } from '../../utils/widget.constants';
import { resolveWidgetStyleColors } from '../../utils/widget-style.utils';

@Component({
  selector: 'wr-video-widget',
  standalone: true,
  template: `
    <div  [style.background]="background()"
          [class.px-3]="layout() === 'standard'"
          [style.background]="background()"
          [style.padding-top.px]="paddingTop()"
          [style.padding-bottom.px]="paddingBottom()"
          [style.text-align]="textAlign()"
    >
      <!-- Title/Subtitles -->
      @if (widget()?.title || widget()?.subtitle && layout() !== 'billboard' && layout() !== 'splash') {
        <div class="px-1 mb-2" [style.text-align]="textAlign()">
          @if (widget()?.title) {
            <div class="title" [style.color]="titleColor()" [style.font-family]="titleFont()">
              {{ widget()?.title }}
            </div>
          }
          @if (widget()?.subtitle) {
            <div class="subtitle" [style.color]="subtitleColor()" [style.font-family]="subtitleFont()">
              {{ widget()?.subtitle }}
            </div>
          }
        </div>
      }
      <div
        style="position: relative; display: inline-block; width: 100%;"
        (mouseenter)="onHover(true)"
        (mouseleave)="onHover(false)"
        (touchstart)="onTouch()"
        role="group"
        [attr.aria-label]="altText()"
      >
        <video
          #videoEl
          [attr.aria-label]="altText()"
          playsinline
          [attr.webkit-playsinline]="''"
          [attr.controls]="showNativeControls() ? '' : null"
          [attr.autoplay]="autoPlay() ? '' : null"
          [attr.muted]="mutedAttr() ? '' : null"
          [attr.loop]="loopAttr() ? '' : null"
          [poster]="poster() || null"
          [style.width]="'100%'"
          [style.height]="videoHeight()"
          [style.object-fit]="objectFit()"
          [style.border-radius.px]="videoCornerRadius()"
          (play)="playing.set(true)"
          [attr.preload]="preloadAttr()"
          (pause)="playing.set(false)"
          (loadeddata)="onVideoReady(); tryAutoplayOnce()"
          (click)="handleVideoClick()"
        >
          <source [attr.src]="src()" [attr.type]="videoMimeType()" />
        </video>

        <!-- Custom overlay controls (only when no native controls AND video is paused) -->
        @if (showOverlayControls() && !playing()) {
          <div
            style="position: absolute; inset: 0; display: flex; justify-content: center; align-items: center;"
            (click)="togglePlay()"
          >
            <button
              type="button"
              (click)="$event.stopPropagation(); togglePlay()"
              [attr.aria-label]="playing() ? 'Pause video' : 'Play video'"
              style="background: rgba(0,0,0,0.45); border: none; border-radius: 9999px; padding: 10px; cursor: pointer;"
            >
              @if (!playing()) {
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M8 5v14l11-7z"></path>
                </svg>
              } @else {
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M6 5h4v14H6zM14 5h4v14h-4z"></path>
                </svg>
              }
            </button>
          </div>
        }

        @if (!showNativeControls()  && controlsVisible()) {
        <button
          type="button"
          (click)="toggleMute(); $event.stopPropagation()"
          [attr.aria-label]="muted() ? 'Unmute video' : 'Mute video'"
          style="
            position:absolute;
            bottom:10px;
            right:10px;
            background:rgba(0,0,0,0.1);
            border:none;
            border-radius:9999px;
            padding:5px;
            cursor:pointer;
          "
        >
          @if (muted()) {
            <img style="width: 15px; height: 15px" src="./assets/icons/volume_off.svg">
          } @else {
            <img style="width: 15px; height: 15px" src="./assets/icons/volume_up.svg">
          }
        </button>
      }

        <!-- Splash wipe-up indicator -->
        @if (layout() === 'splash' && widget()?.showMoreIndicator) {
          <div class="wr-swipe-indicator">
            <img
              src="assets/icons/swipe-up.svg"
              aria-hidden="true"
              alt="Swipe up"
              width="30"
              height="30"
            />
          </div>
        }

        @if (layout() === 'standard' && controlsVisible() && !showNativeControls()) {
          <button
            type="button"
            (click)="toggleFullscreen(); $event.stopPropagation()"
            aria-label="Toggle fullscreen"
            style="
              position:absolute;
              top:10px;
              left:10px;
              background:rgba(0,0,0,0.10);
              border:none;
              border-radius:9999px;
              padding:5px;
              cursor:pointer;
            "
          >
            <img style="width: 15px; height: 15px" src="./assets/icons/fullscreen.svg">
          </button>
        }

      </div>
    </div>
  `,
})
export class VideoWidgetComponent {

  constructor() {
    this.isTouchDevice.set(
      'ontouchstart' in window || navigator.maxTouchPoints > 0
    );
  }

  private widgetSig = signal<VideoWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);
  private hideTimer: any;

  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  playing = signal(false);
  muted = signal(false);
  controlsVisible = signal(false);
  isTouchDevice = signal(false);

  @Input() set json(value: VideoWidget) {
    this.widgetSig.set(value);
    this.muted.set(!!value?.muted);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  widget = computed(() => this.widgetSig());

  private colors = computed(() => {
    const t = this.themeSig();
    if (!t) return null;
    return t.isDark ? t.darkColors : t.lightColors;
  });

  // layout
  
  layout = computed<VideoLayout>(() => {
    const w = this.widget();
    if (!w) return 'standard';

    // legacy types
    if (w.type === 'billboardVideo') return 'billboard';
    if (w.type === 'splashVideo') return 'splash';

    return w.layout ?? 'standard';
  });

  // styles
  mergedColors = computed<WidgetColors>(() => {
    const w = this.widget();
    const theme = this.themeSig();
    const isDark = theme?.isDark ?? false;

    const themeColors: WidgetColors = theme
      ? { ...theme[isDark ? 'darkColors' : 'lightColors'] }
      : {};

    const widgetColors: WidgetColors = resolveWidgetStyleColors(w?.widgetStyle, isDark);

    return {
      accent: widgetColors.accent ?? themeColors.accent ?? DEFAULT_COLORS.accent,
      title: widgetColors.title ?? themeColors.title ?? DEFAULT_COLORS.title,
      subtitle: widgetColors.subtitle ?? themeColors.subtitle ?? DEFAULT_COLORS.subtitle,
      primary: widgetColors.primary ?? themeColors.primary ?? DEFAULT_COLORS.primary,
      background: widgetColors.background ?? themeColors.background ?? DEFAULT_COLORS.background,
    };
  });

    background = computed(() => this.mergedColors()?.background);
    titleColor = computed(() => this.mergedColors()?.title);
    subtitleColor = computed(() => this.mergedColors()?.subtitle);
    paddingTop = computed(() =>
      this.widget()?.widgetStyle?.padding?.top ?? DEFAULT_PADDING.top
    );

    paddingBottom = computed(() =>
      this.widget()?.widgetStyle?.padding?.bottom ?? DEFAULT_PADDING.bottom
    );

    textAlign = computed(() => {
      const align = this.widget()?.widgetStyle?.contentAlignment;

      if (align === 'leading') return 'left';
      if (align === 'center') return 'center';
      if (align === 'trailing') return 'right';

      return 'left';
    });

  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');

  videoCornerRadius = computed(() => {
    const base = this.themeSig()?.cornerRadius ?? 0;
    return this.layout() === 'standard' ? base : 0; // billboard/splash = edge to edge
  });

  // attributes

  src = computed(() => this.widget()?.url ?? '');
  poster = computed(() => this.widget()?.coverImageURL ?? null);

  altText = computed(() => this.widget()?.altTitle ?? this.widget()?.id ?? 'video');

  autoPlay = computed(() => !!this.widget()?.autoPlay);
  loopAttr = computed(() => !!this.widget()?.autoRePlay);

  // Match old behavior:
  // - billboard: autoplay -> muted required
  // - otherwise: muted flag respected; if autoplay true, allow muted too
  mutedAttr = computed(() => {
    const w = this.widget();
    if (!w) return false;
    if (w.autoPlay) return true;
    return !!w.muted;
  });

  showNativeControls = computed(() => !!this.widget()?.videoControls);

  showOverlayControls = computed(() => {
    const w = this.widget();
    if (!w) return false;
    if (!w.videoControls) return true;
    return false;
  });

  videoMimeType = computed(() => {
    const url = this.src();
    if (!url) return 'video/mp4';
    const lower = url.toLowerCase();
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.mov')) return 'video/mp4';
    return 'video/mp4';
  });


  preloadAttr = computed(() => {
    return 'metadata';
  });

  tryAutoplayOnce() {
    const el = this.videoEl?.nativeElement;
    const w = this.widget();
    if (!el || !w?.autoPlay) return;

    el.muted = true;
    el.playsInline = true;

    const playPromise = el.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch((err: any) => {
        console.warn('Autoplay was blocked by the browser:', err);
      });
    }
  }

  handleVideoClick() {
    if (this.showNativeControls()) return;
    this.togglePlay();
  }

  // layout-dependent sizing
  videoHeight = computed(() => (this.layout() === 'splash' ? '100vh' : '100%')); //TODO: reduce vh?
  objectFit = computed(() => (this.layout() === 'splash' ? 'cover' : 'contain'));

  // actions
  togglePlay() {
    const el = this.videoEl?.nativeElement;
    if (!el) return;

    if (el.paused) el.play();
    else el.pause();
  }

  toggleMute() {
    const el = this.videoEl?.nativeElement;
    if (!el) return;

    const next = !this.muted();
    this.muted.set(next);
    el.muted = next;
  }

  toggleFullscreen() {
    const el = this.videoEl?.nativeElement;
    if (!el) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }

  onHover(state: boolean) {
    if (this.isTouchDevice()) return;
    this.controlsVisible.set(state);
  }

  onTouch() {
    if (!this.isTouchDevice()) return;
    this.controlsVisible.set(true);
  }

  onVideoReady() {
    const el = this.videoEl?.nativeElement;
    if (!el) return;
    el.muted = this.muted();
  }

}
